'use client'

import Link from 'next/link'
import { Menu, Sparkles, CreditCard, LogIn } from 'lucide-react'
import { Logo } from '@/components/logo'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { ThemeToggle } from '@/components/theme-toggle'
import { LanguageToggle } from '@/components/language-toggle'
import { UserMenu } from '@/components/auth/UserMenu'
import { cn } from '@/lib/utils'

export function Header() {
	const t = useTranslations('nav')
	const [scrolled, setScrolled] = useState(false)
	const { isAuthenticated, isLoading } = useAuth()
	const pathname = usePathname()
	const isDashboard = pathname?.startsWith('/dashboard')

	useEffect(() => {
		const handleScroll = () => {
			const simpleBarContent = document.querySelector('.simplebar-content-wrapper')
			const scrollTop = simpleBarContent?.scrollTop || window.scrollY
			setScrolled(scrollTop > 20)
		}

		window.addEventListener('scroll', handleScroll, { passive: true })
		const simpleBarContent = document.querySelector('.simplebar-content-wrapper')
		if (simpleBarContent) {
			simpleBarContent.addEventListener('scroll', handleScroll, { passive: true })
		}
		handleScroll()

		return () => {
			window.removeEventListener('scroll', handleScroll)
			if (simpleBarContent) {
				simpleBarContent.removeEventListener('scroll', handleScroll)
			}
		}
	}, [])

	return (
		<header className="fixed top-0 left-0 right-0 z-50 md:px-4 md:pt-3 lg:pt-4">
			<div
				className={cn(
					'mx-auto flex h-14 items-center justify-between px-4 md:px-6 md:rounded-xl border transition-all duration-300',
					isDashboard ? 'max-w-[1600px]' : 'max-w-6xl',
					scrolled
						? 'bg-background/80 backdrop-blur-md border-border/50 shadow-lg'
						: 'bg-transparent border-transparent'
				)}
			>
				<Link href="/" className="flex items-center gap-2">
					<div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600">
						<LogoAlt className="h-5 w-5 text-white" />
					</div>
					<span className="text-lg font-bold">Evuno</span>
				</Link>

				{!isDashboard && (
					<nav className="hidden md:flex items-center gap-6">
						<Link href="/#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
							{t('features')}
						</Link>
						<Link href="/#models" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
							{t('models')}
						</Link>
						<Link href="/#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
							{t('pricing')}
						</Link>
						<Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
							{t('docs')}
						</Link>
					</nav>
				)}

				<div className="hidden md:flex items-center gap-1">
					<LanguageToggle />
					<ThemeToggle />
					<UserMenu />
				</div>

				<div className="flex md:hidden items-center">
					<Sheet>
						<SheetTrigger asChild>
							<Button variant="ghost" size="icon">
								<Menu className="h-5 w-5" />
							</Button>
						</SheetTrigger>
						<SheetContent side="right" className="w-[280px] flex flex-col p-0">
							<SheetHeader className="sr-only">
								<SheetTitle>{t('menu')}</SheetTitle>
							</SheetHeader>

							<div className="flex items-center gap-2 px-6 py-5 border-b">
								<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
									<LogoAlt className="h-6 w-6 text-white" />
								</div>
								<span className="text-lg font-bold">Evuno</span>
							</div>

							<nav className="flex-1 px-3 py-4">
								{!isDashboard && (
									<div className="space-y-1">
										<Link
											href="/#features"
											className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
										>
											<Sparkles className="h-4 w-4 text-muted-foreground" />
											{t('features')}
										</Link>
										<Link
											href="/#pricing"
											className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
										>
											<CreditCard className="h-4 w-4 text-muted-foreground" />
											{t('pricing')}
										</Link>
									</div>
								)}

								<div className="mt-6 pt-6 border-t">
									{isLoading ? (
										<div className="flex items-center gap-3 px-3 py-2.5">
											<div className="h-4 w-4 rounded-full bg-muted animate-pulse" />
											<div className="h-4 w-20 rounded bg-muted animate-pulse" />
										</div>
									) : isAuthenticated ? (
										<div className="px-1">
											<UserMenu />
										</div>
									) : (
										<Link
											href="/auth/signin"
											className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
										>
											<LogIn className="h-4 w-4 text-muted-foreground" />
											{t('signIn')}
										</Link>
									)}
								</div>
							</nav>

							<div className="px-6 py-4 border-t bg-muted/30">
								<div className="flex items-center justify-between">
									<span className="text-xs text-muted-foreground font-medium">{t('preferences')}</span>
									<div className="flex items-center gap-2">
										<LanguageToggle />
										<ThemeToggle />
									</div>
								</div>
							</div>
						</SheetContent>
					</Sheet>
				</div>
			</div>
		</header>
	)
}
